// ===========================================
// Settings Page
// ===========================================
// User settings with Profile and Preferences tabs

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  User, Shield, Palette, Save, Loader, Check
} from 'lucide-react';
import './Settings.scss';

// ===========================================
// Predefined color themes for the app
// ===========================================
const COLOR_THEMES = [
  { name: 'Amber', value: '#f59e0b', hover: '#fbbf24' },
  { name: 'Green', value: '#22c55e', hover: '#4ade80' },
  { name: 'Blue', value: '#3b82f6', hover: '#60a5fa' },
  { name: 'Purple', value: '#a855f7', hover: '#c084fc' },
  { name: 'Pink', value: '#ec4899', hover: '#f472b6' },
  { name: 'Red', value: '#ef4444', hover: '#f87171' },
  { name: 'Cyan', value: '#06b6d4', hover: '#22d3ee' },
  { name: 'Teal', value: '#14b8a6', hover: '#2dd4bf' },
  { name: 'Indigo', value: '#6366f1', hover: '#818cf8' },
  { name: 'Orange', value: '#f97316', hover: '#fb923c' },
];

const Settings = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Profile form state - simplified to just name
  const [profile, setProfile] = useState({
    name: user?.name || ''
  });
  
  // Password form state
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  
  // Color theme state - load from user profile, then localStorage, then default to amber
  const [selectedColor, setSelectedColor] = useState(() => {
    return user?.preferences?.accentColor || localStorage.getItem('devpad_accent_color') || '#f59e0b';
  });

  // ===========================================
  // Auto-clear success messages after 3 seconds
  // ===========================================
  useEffect(() => {
    if (message.type === 'success' && message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // ===========================================
  // Apply color theme on mount and when changed
  // ===========================================
  useEffect(() => {
    applyColorTheme(selectedColor);
  }, [selectedColor]);

  // Apply color theme to CSS variables
  const applyColorTheme = (color) => {
    const theme = COLOR_THEMES.find(t => t.value === color) || COLOR_THEMES[0];
    document.documentElement.style.setProperty('--color-accent', theme.value);
    document.documentElement.style.setProperty('--color-accent-hover', theme.hover);
    document.documentElement.style.setProperty('--color-accent-muted', `${theme.value}1f`);
    document.documentElement.style.setProperty('--color-border-accent', `${theme.value}4d`);
    localStorage.setItem('devpad_accent_color', color);
  };

  // Handle color theme selection - saves to both localStorage and user profile
  const handleColorChange = async (color) => {
    setSelectedColor(color);
    
    // Save to user profile for cross-device sync
    try {
      await updateProfile({
        preferences: {
          ...user?.preferences,
          accentColor: color
        }
      });
    } catch (err) {
      // Silently fail - localStorage is already saved as backup
      console.error('Failed to save color preference to profile:', err);
    }
  };

  // Handle profile update - simplified
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    const result = await updateProfile({ name: profile.name });
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } else {
      setMessage({ type: 'error', text: result.error });
    }
    
    setSaving(false);
  };

  // Handle password change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    if (passwords.new !== passwords.confirm) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setSaving(false);
      return;
    }
    
    if (passwords.new.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      setSaving(false);
      return;
    }
    
    const result = await changePassword(passwords.current, passwords.new);
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswords({ current: '', new: '', confirm: '' });
    } else {
      setMessage({ type: 'error', text: result.error });
    }
    
    setSaving(false);
  };

  return (
    <div className="settings">
      {/* Header */}
      <header className="settings__header">
        <h1>Settings</h1>
        <p>Manage your account and preferences</p>
      </header>

      {/* Settings Layout */}
      <div className="settings__layout">
        {/* Sidebar Navigation - Profile and Preferences only */}
        <nav className="settings__nav">
          <button 
            className={activeTab === 'profile' ? 'active' : ''}
            onClick={() => setActiveTab('profile')}
          >
            <User size={18} /> Profile
          </button>
          <button 
            className={activeTab === 'preferences' ? 'active' : ''}
            onClick={() => setActiveTab('preferences')}
          >
            <Palette size={18} /> Preferences
          </button>
        </nav>

        {/* Content */}
        <div className="settings__content">
          {/* Message */}
          {message.text && (
            <div className={`settings__message settings__message--${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Profile Tab - Simplified: Name, Email, Change Password */}
          {activeTab === 'profile' && (
            <div className="settings__form">
              <h2>Profile Information</h2>
              <p className="settings__form-desc">
                Manage your account details.
              </p>

              {/* Name & Email Section */}
              <form onSubmit={handleProfileSubmit}>
                <div className="settings__field">
                  <label htmlFor="name">User Name</label>
                  <input
                    id="name"
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    placeholder="Your name"
                  />
                </div>

                <div className="settings__field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="settings__field--disabled"
                  />
                  <span className="settings__field-hint">Email cannot be changed</span>
                </div>

                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                  Save Changes
                </button>
              </form>

              {/* Change Password Section */}
              <div className="settings__section">
                <h3><Shield size={16} /> Change Password</h3>
                <p>Keep your account secure by using a strong password.</p>
                
                <form onSubmit={handlePasswordSubmit}>
                  <div className="settings__field">
                    <label htmlFor="current">Current Password</label>
                    <input
                      id="current"
                      type="password"
                      value={passwords.current}
                      onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                      placeholder="Enter current password"
                    />
                  </div>

                  <div className="settings__field">
                    <label htmlFor="new">New Password</label>
                    <input
                      id="new"
                      type="password"
                      value={passwords.new}
                      onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                      placeholder="Enter new password"
                    />
                  </div>

                  <div className="settings__field">
                    <label htmlFor="confirm">Confirm New Password</label>
                    <input
                      id="confirm"
                      type="password"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                      placeholder="Confirm new password"
                    />
                  </div>

                  <button type="submit" className="btn-primary" disabled={saving}>
                    {saving ? <Loader size={16} className="animate-spin" /> : <Shield size={16} />}
                    Update Password
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Preferences Tab - Color Theme Picker */}
          {activeTab === 'preferences' && (
            <div className="settings__form">
              <h2>Preferences</h2>
              <p className="settings__form-desc">
                Customize your DevPad experience.
              </p>

              {/* Color Theme Picker */}
              <div className="settings__field">
                <label>Accent Color</label>
                <p className="settings__field-desc">Choose a color theme for the app interface.</p>
                <div className="settings__color-grid">
                  {COLOR_THEMES.map((theme) => (
                    <button
                      key={theme.value}
                      type="button"
                      className={`settings__color-swatch ${selectedColor === theme.value ? 'settings__color-swatch--active' : ''}`}
                      style={{ backgroundColor: theme.value }}
                      onClick={() => handleColorChange(theme.value)}
                      title={theme.name}
                    >
                      {selectedColor === theme.value && <Check size={16} />}
                    </button>
                  ))}
                </div>
                <span className="settings__field-hint">
                  Current: {COLOR_THEMES.find(t => t.value === selectedColor)?.name || 'Custom'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Settings;

